import { useState, useEffect, useCallback, useMemo } from 'react';
import { authHeaders, Toast } from './listPageStyles';
import { ListPageShell } from './ui';
import { KpiCard } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

// Satış Maratonu — mobildeki "Satış Maratonu" (Satış Süper Ligi) ekranının web karşılığı.
// Aynı backend'i (GET /api/leaderboard?period=month|week) tüketir → mobil ile birebir veri.
const API_BASE = '/api/leaderboard';

const PERIODS = [
  { value: 'month', label: 'Bu Ay' },
  { value: 'week', label: 'Bu Hafta' },
];

// Podyum madalyon gradyanları (mobil GoldGradient / SilverGradient / BronzeGradient paritesi).
const PODIUM = [
  { grad: 'linear-gradient(135deg,#f7d774,#e0a106)', ring: '#e0a106', label: '1.' },
  { grad: 'linear-gradient(135deg,#e7eaf0,#aab2c0)', ring: '#aab2c0', label: '2.' },
  { grad: 'linear-gradient(135deg,#e7b07a,#b9712f)', ring: '#b9712f', label: '3.' },
];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapList(payload) {
  const data = pick(payload, 'data', 'Data');
  if (Array.isArray(data)) return data;
  const items = pick(data, 'items', 'Items');
  return Array.isArray(items) ? items : [];
}

const fmtMoney = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v || 0);
const fmtNum = (v) => new Intl.NumberFormat('tr-TR').format(v || 0);
const initial = (name) => (name || '—').trim().charAt(0).toLocaleUpperCase('tr-TR') || '—';

function PodiumSpot({ entry, idx }) {
  const cfg = PODIUM[idx];
  const big = idx === 0;
  const size = big ? 76 : 58;
  const pedestal = big ? 104 : idx === 1 ? 78 : 60;
  if (!entry) return <div style={{ flex: 1 }} />;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <div
        style={{
          width: size, height: size, borderRadius: '50%', background: cfg.grad,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: big ? 26 : 20,
          boxShadow: `0 6px 16px rgba(0,0,0,.18)`, border: '3px solid rgba(255,255,255,.6)',
        }}
      >
        {initial(entry.name)}
      </div>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
        {entry.name}
      </div>
      <div style={{ color: 'rgba(255,255,255,.9)', fontSize: 12, fontWeight: 600 }}>{fmtMoney(entry.totalSales)}</div>
      <div
        style={{
          width: '100%', height: pedestal, marginTop: 4, background: cfg.grad,
          borderRadius: '10px 10px 0 0', display: 'flex', justifyContent: 'center',
          paddingTop: 8, color: '#fff', fontWeight: 800, fontSize: 22,
        }}
      >
        {cfg.label}
      </div>
    </div>
  );
}

function PodiumHero({ top }) {
  if (!top.length) return null;
  // Sıralama: 2. sol, 1. orta (yüksek), 3. sağ — mobil podyum paritesi.
  const ordered = [top[1], top[0], top[2]];
  const orderIdx = [1, 0, 2];
  return (
    <div
      style={{
        background: 'linear-gradient(135deg,#3f2d8c 0%,#5b3fb0 45%,#7c4fd0 100%)',
        borderRadius: 20, padding: '22px 20px 0', marginBottom: 18, overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#fff', fontWeight: 800, fontSize: 16, marginBottom: 18 }}>
        <span role="img" aria-label="kupa">🏆</span> Bu Dönemin Şampiyonları
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, maxWidth: 560, margin: '0 auto' }}>
        {ordered.map((e, i) => (
          <PodiumSpot key={orderIdx[i]} entry={e} idx={orderIdx[i]} />
        ))}
      </div>
    </div>
  );
}

function RankRow({ e, isTop3 }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--border-color,#e5e7eb)' }}>
      <td style={{ padding: '10px 12px', width: 56 }}>
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 30, height: 30, borderRadius: '50%', fontWeight: 800, fontSize: 13,
            background: isTop3 ? PODIUM[e.rank - 1].grad : 'var(--grid-canvas,#eef2f9)',
            color: isTop3 ? '#fff' : 'var(--text-secondary,#4b5563)',
          }}
        >
          {e.rank}
        </span>
      </td>
      <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--text-primary,#111827)' }}>{e.name}</td>
      <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-secondary,#4b5563)', fontWeight: 600 }}>
        {fmtNum(e.points)} puan
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, color: '#5b3fb0' }}>{fmtMoney(e.totalSales)}</td>
    </tr>
  );
}

export function LeaderboardPage() {
  usePageMeta('Satış Maratonu', 'Temsilci satış sıralaması ve şampiyonlar — mobil Satış Maratonu ile aynı veri.');
  const [period, setPeriod] = useState('month');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?period=${period}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Sıralama alınamadı (${res.status})`);
      const payload = await res.json();
      const items = unwrapList(payload).map((r) => ({
        rank: pick(r, 'rank', 'Rank') ?? 0,
        repId: String(pick(r, 'repId', 'RepId') ?? ''),
        name: pick(r, 'name', 'Name') ?? '—',
        totalSales: Number(pick(r, 'totalSales', 'TotalSales') ?? 0),
        points: Number(pick(r, 'points', 'Points') ?? 0),
      }));
      setRows(items);
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Sıralama alınamadı' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); } }, [toast]);

  const totals = useMemo(() => {
    const sales = rows.reduce((s, r) => s + r.totalSales, 0);
    const points = rows.reduce((s, r) => s + r.points, 0);
    return { reps: rows.length, sales, points, leader: rows[0]?.name || '—' };
  }, [rows]);

  const periodLabel = PERIODS.find((p) => p.value === period)?.label || '';

  const actions = (
    <div style={{ display: 'inline-flex', gap: 4, background: 'var(--grid-canvas,#eef2f9)', padding: 4, borderRadius: 10 }}>
      {PERIODS.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => setPeriod(p.value)}
          style={{
            border: 'none', cursor: 'pointer', padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13,
            background: period === p.value ? '#5b3fb0' : 'transparent',
            color: period === p.value ? '#fff' : 'var(--text-secondary,#4b5563)',
          }}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <Toast toast={toast} />
      <ListPageShell
        title="Satış Maratonu"
        subtitle="Temsilci satış sıralaması — mobil uygulamadaki Satış Maratonu ile aynı veri."
        meta={periodLabel}
        actions={actions}
      >
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary,#4b5563)', fontWeight: 700 }}>Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary,#4b5563)', fontWeight: 700 }}>
            Bu dönem için sıralama verisi yok.
          </div>
        ) : (
          <div style={{ padding: 4 }}>
            <PodiumHero top={rows.slice(0, 3)} />

            <div className="c360-kpi-grid" style={{ marginBottom: 18 }}>
              <KpiCard label="Yarışan Temsilci" value={fmtNum(totals.reps)} />
              <KpiCard label="Toplam Ciro" value={fmtMoney(totals.sales)} hint={periodLabel} />
              <KpiCard label="Toplam Puan" value={fmtNum(totals.points)} hint="ciro / 100" />
              <KpiCard label="Lider" value={totals.leader} hint="1. sıra" />
            </div>

            <div style={{ border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--grid-canvas,#eef2f9)', color: 'var(--text-secondary,#4b5563)', textAlign: 'left' }}>
                    <th style={{ padding: '10px 12px', fontWeight: 700 }}>Sıra</th>
                    <th style={{ padding: '10px 12px', fontWeight: 700 }}>Temsilci</th>
                    <th style={{ padding: '10px 12px', fontWeight: 700, textAlign: 'right' }}>Puan</th>
                    <th style={{ padding: '10px 12px', fontWeight: 700, textAlign: 'right' }}>Ciro</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <RankRow key={e.repId || e.rank} e={e} isTop3={e.rank <= 3} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ListPageShell>
    </>
  );
}

export default LeaderboardPage;
